import { z } from 'zod';
import {
  ADMIN_ROLES,
  MAX_FEE_BPS,
  PAYMENT_METHODS,
  TOPUP_STATUSES,
  WITHDRAWAL_STATUSES,
} from '../constants';
import { objectIdSchema, isoDateSchema, paginationQuerySchema, santimSchema, text } from './common';

export const adminLoginSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(1).max(200),
  /** 6-digit TOTP or a backup code. Omitted on the very first login (enrollment flow). */
  totp: z
    .string()
    .trim()
    .regex(/^(\d{6}|[A-Za-z0-9-]{8,16})$/)
    .optional(),
});
export type AdminLoginBody = z.infer<typeof adminLoginSchema>;

export const totpCodeSchema = z.object({ code: z.string().regex(/^\d{6}$/) });

export const ADMIN_PASSWORD_MIN_LENGTH = 12;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1).max(200),
    newPassword: z.string().min(ADMIN_PASSWORD_MIN_LENGTH).max(200),
  })
  .refine((v) => v.newPassword !== v.currentPassword, 'New password must be different');
export type ChangePasswordBody = z.infer<typeof changePasswordSchema>;

export const createAdminSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  name: text(2, 100),
  role: z.enum(ADMIN_ROLES),
});
export type CreateAdminBody = z.infer<typeof createAdminSchema>;

export const updateAdminSchema = z
  .object({
    name: text(2, 100).optional(),
    role: z.enum(ADMIN_ROLES).optional(),
    status: z.enum(['active', 'disabled']).optional(),
    resetTotp: z.literal(true).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Provide at least one field');
export type UpdateAdminBody = z.infer<typeof updateAdminSchema>;

export const adminUsersQuerySchema = paginationQuerySchema.extend({ q: text(1, 100).optional() });

export const adminAdjustmentSchema = z.object({
  /** Signed, non-zero santim. */
  amount: z
    .number()
    .int()
    .safe()
    .refine((v) => v !== 0, 'amount must not be zero'),
  reason: text(3, 500),
});
export type AdminAdjustmentBody = z.infer<typeof adminAdjustmentSchema>;

export const approveTopUpSchema = z.object({ creditedAmount: santimSchema.optional() });
export type ApproveTopUpBody = z.infer<typeof approveTopUpSchema>;

export const adminTopUpsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(TOPUP_STATUSES).default('pending'),
});

export const adminWithdrawalsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(WITHDRAWAL_STATUSES).default('pending'),
});

export const completeWithdrawalSchema = z.object({ adminTransferRef: text(3, 100) });
export type CompleteWithdrawalBody = z.infer<typeof completeWithdrawalSchema>;

export const depositAccountSchema = z.object({
  method: z.enum(PAYMENT_METHODS),
  accountName: text(1, 100),
  accountNumber: text(1, 64),
  instructions: z.object({ en: text(1, 1000), am: text(1, 1000) }),
});

export const configPatchSchema = z
  .object({
    feeBps: z.number().int().min(0).max(MAX_FEE_BPS),
    minStake: santimSchema,
    maxStake: santimSchema,
    maxUserPerMarket: santimSchema,
    minTopUp: santimSchema,
    minWithdrawal: santimSchema,
    maxPendingWithdrawals: z.number().int().min(1).max(20),
    requireTwoPersonResolution: z.boolean(),
    largeTopUpThreshold: santimSchema,
    largeWithdrawalThreshold: santimSchema,
    minTurnoverRatio: z.number().min(0).max(10),
    depositAccounts: z.array(depositAccountSchema).max(10),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Provide at least one field')
  .refine(
    (v) => v.minStake === undefined || v.maxStake === undefined || v.minStake <= v.maxStake,
    'minStake must not exceed maxStake',
  );
export type ConfigPatchBody = z.infer<typeof configPatchSchema>;

export const auditQuerySchema = paginationQuerySchema.extend({
  actorId: objectIdSchema.optional(),
  action: text(1, 100).optional(),
  targetType: text(1, 50).optional(),
  targetId: objectIdSchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;
